import { useState, useEffect, useRef, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { contactInfo } from "@/mocks/homeData";
import { isSubmissionRateLimited, registerSubmission } from "@/utils/formGuard";

const serviceOptions = [
  "Consultoria",
  "Consultoria de Seguridad",
  "CyberSOC",
  "Service Desk",
  "Datacenter e Infraestructura",
  "Ingenieria de Productos",
  "Secure Academy",
  "Data Security",
  "Endpoint Security",
  "Cloud Security",
  "Application Security",
  "Networking Security",
  "Ciberinteligencia",
  "Otro",
];

export default function Contact() {
  const { t } = useTranslation("common");
  const [formStatus, setFormStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");
  const sectionRef = useRef<HTMLElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          obs.disconnect();
        }
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) obs.observe(sectionRef.current);
    return () => obs.disconnect();
  }, []);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus("submitting");
    setFormError("");

    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get("company_alt")?.toString().trim() || "";
    if (honeypot) {
      setFormStatus("success");
      form.reset();
      return;
    }

    if (isSubmissionRateLimited()) {
      setFormStatus("error");
      setFormError(t("contact.rateLimited"));
      return;
    }

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key !== "company_alt") {
        payload.append(key, value.toString());
      }
    });

    try {
      const response = await fetch("https://readdy.ai/api/form/d9mfr5gbdkpk99j7gang", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });

      const responseText = await response.text();
      let parsed: Record<string, unknown> | null = null;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      const serverMsg =
        (parsed?.meta as Record<string, string>)?.message ||
        (parsed?.meta as Record<string, string>)?.detail ||
        (parsed?.message as string) ||
        responseText;

      const isSpam =
        typeof serverMsg === "string" &&
        (serverMsg.toLowerCase().includes("spam") || serverMsg.toLowerCase().includes("form data is spam"));

      if (!response.ok || isSpam || (parsed && (parsed.code as string) !== "OK")) {
        setFormStatus("error");
        setFormError(serverMsg || t("contact.errorDefault"));
        return;
      }

      setFormStatus("success");
      registerSubmission();
      form.reset();
    } catch {
      setFormStatus("error");
      setFormError(t("contact.connectionError"));
    }
  };

  const socials = [
    { icon: "ri-linkedin-fill", href: contactInfo.social.linkedin, label: "LinkedIn" },
    { icon: "ri-facebook-fill", href: contactInfo.social.facebook, label: "Facebook" },
    { icon: "ri-instagram-fill", href: contactInfo.social.instagram, label: "Instagram" },
    { icon: "ri-youtube-fill", href: contactInfo.social.youtube, label: "YouTube" },
    { icon: "ri-tiktok-fill", href: contactInfo.social.tiktok, label: "TikTok" },
  ];

  const inputBase =
    "w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-foreground-100 text-sm placeholder:text-foreground-500/40 focus:outline-none focus:ring-2 focus:ring-primary-400/30 focus:border-primary-400/40 transition-all";
  const labelBase =
    "block text-[11px] font-label font-semibold text-foreground-400/70 mb-1.5 uppercase tracking-wider";

  return (
    <section id="contact" ref={sectionRef} className="relative py-14 md:py-20 bg-[#0B1628] overflow-hidden">
      {/* Human background image */}
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=Friendly%20customer%20support%20specialist%20wearing%20a%20headset%20and%20smiling%20warmly%20at%20the%20camera%20in%20a%20bright%20modern%20office%20with%20green%20plants%20and%20large%20windows%2C%20soft%20natural%20daylight%2C%20welcoming%20and%20approachable%20atmosphere%2C%20professional%20corporate%20photography%2C%20human%20centered%20composition%2C%20shallow%20depth%20of%20field&width=1920&height=1080&seq=contact-bg-human-team-01&orientation=landscape&nocache=true"
          srcSet="https://readdy.ai/api/search-image?query=Friendly%20customer%20support%20specialist%20wearing%20a%20headset%20and%20smiling%20warmly%20at%20the%20camera%20in%20a%20bright%20modern%20office%20with%20green%20plants%20and%20large%20windows%2C%20soft%20natural%20daylight%2C%20welcoming%20and%20approachable%20atmosphere%2C%20professional%20corporate%20photography%2C%20human%20centered%20composition%2C%20shallow%20depth%20of%20field%201920wquery=Friendly%20customer%20support%20specialist%20wearing%20a%20headset%20and%20smiling%20warmly%20at%20the%20camera%20in%20a%20bright%20modern%20office%20with%20green%20plants%20and%20large%20windows%2C%20soft%20natural%20daylight%2C%20welcoming%20and%20approachable%20atmosphere%2C%20professional%20corporate%20photography%2C%20human%20centered%20composition%2C%20shallow%20depth%20of%20field%201280w%2C%20https:%2F%2Freaddy.ai%2Fapi%2Fsearch-image%3Fwidth=1920query=Friendly%20customer%20support%20specialist%20wearing%20a%20headset%20and%20smiling%20warmly%20at%20the%20camera%20in%20a%20bright%20modern%20office%20with%20green%20plants%20and%20large%20windows%2C%20soft%20natural%20daylight%2C%20welcoming%20and%20approachable%20atmosphere%2C%20professional%20corporate%20photography%2C%20human%20centered%20composition%2C%20shallow%20depth%20of%20field%20640w%2C%20https:%2F%2Freaddy.ai%2Fapi%2Fsearch-image%3Fwidth=1280&width=640&height=360&seq=contact-bg-human-team-01&orientation=landscape&height=720&seq=contact-bg-human-team-01&orientation=landscape&height=1080&seq=contact-bg-human-team-01&orientation=landscape"
          sizes="100vw"
          alt={t("contact.title")}
          className="w-full h-full object-cover object-center"
          loading="lazy"
          decoding="async"
        />
        <div className="absolute inset-0 bg-[#0B1628]/35" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0B1628] via-[#0B1628]/15 to-[#0B1628]" />
      </div>
      {/* Tech grid background */}
      <div
        className="absolute inset-0 pointer-events-none opacity-[0.03]"
        style={{
          backgroundImage:
            "linear-gradient(oklch(var(--primary-500) / 0.4) 1px, transparent 1px), linear-gradient(90deg, oklch(var(--primary-500) / 0.4) 1px, transparent 1px)",
          backgroundSize: "80px 80px",
        }}
      />
      {/* Radial glows */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-primary-500/5 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 right-0 w-[600px] h-[300px] bg-accent-500/5 rounded-full blur-3xl pointer-events-none" />

      {/* Top line */}
      <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-primary-400/30 to-transparent" />

      <div className="w-full px-4 sm:px-6 lg:px-10 relative z-10">
        {/* Header */}
        <div
          className={`text-center mb-8 md:mb-10 transition-all duration-1000 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
        >
          <h2 className="font-heading font-bold text-2xl md:text-4xl lg:text-5xl text-foreground-50 mb-3">
            {t("contact.title")}{" "}
            <span className="bg-gradient-to-r from-accent-300 via-primary-400 to-accent-400 bg-clip-text text-transparent">
              {t("contact.highlight")}
            </span>
          </h2>
          <p className="text-sm text-foreground-400/60 max-w-lg mx-auto leading-relaxed">
            {t("contact.subtitle")}
          </p>
        </div>

        <div
          className={`max-w-6xl mx-auto transition-all duration-1000 delay-200 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
        >
          {/* FORM */}
          <div className="relative rounded-2xl border border-white/10 bg-[#0B1628]/65 backdrop-blur-md p-5 sm:p-7 md:p-10 overflow-hidden mb-10">
            {/* Subtle glow */}
            <div className="absolute -inset-px rounded-2xl bg-gradient-to-br from-primary-400/5 via-transparent to-accent-400/5 opacity-0 hover:opacity-100 transition-opacity duration-700 pointer-events-none" />

            {/* Corner brackets */}
            <div className="absolute top-4 left-4 w-6 h-6 border-t border-l border-primary-400/20 pointer-events-none" />
            <div className="absolute top-4 right-4 w-6 h-6 border-t border-r border-primary-400/20 pointer-events-none" />
            <div className="absolute bottom-4 left-4 w-6 h-6 border-b border-l border-primary-400/20 pointer-events-none" />
            <div className="absolute bottom-4 right-4 w-6 h-6 border-b border-r border-primary-400/20 pointer-events-none" />

            {formStatus === "success" ? (
              <div className="text-center py-12 relative z-10">
                <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-primary-500/10 border border-primary-400/20 mb-5">
                  <i className="ri-check-line text-2xl text-primary-300" />
                </div>
                <h3 className="font-heading font-semibold text-lg text-foreground-50 mb-2">
                  {t("contact.successTitle")}
                </h3>
                <p className="text-sm text-foreground-400/60 mb-6">
                  {t("contact.successMsg")}
                </p>
                <button
                  type="button"
                  onClick={() => setFormStatus("idle")}
                  className="inline-flex items-center px-6 py-2.5 rounded-full border border-primary-400/30 bg-primary-500/10 text-primary-300 font-label font-semibold text-sm hover:bg-primary-500/15 transition-all cursor-pointer"
                >
                  {t("contact.sendAnother")}
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} data-readdy-form className="relative z-10">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  {/* Columna izquierda: datos personales */}
                  <div className="lg:col-span-2 space-y-4">
                    {/* Fila 1: Nombre completo + Email */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="contact-fullname" className={labelBase}>
                          {t("contact.fullname")} <span className="text-rose-400">*</span>
                        </label>
                        <input
                          type="text"
                          id="contact-fullname"
                          name="fullname"
                          required
                          className={inputBase}
                          placeholder={t("contact.fullnamePlaceholder")}
                        />
                      </div>
                      <div>
                        <label htmlFor="contact-email" className={labelBase}>
                          {t("contact.emailLabel")} <span className="text-rose-400">*</span>
                        </label>
                        <input
                          type="email"
                          id="contact-email"
                          name="email"
                          required
                          className={inputBase}
                          placeholder={t("contact.emailPlaceholder")}
                        />
                      </div>
                    </div>

                    {/* Fila 2: Telefono + RUC */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="contact-telefono" className={labelBase}>
                          {t("contact.phone")} <span className="text-rose-400">*</span>
                        </label>
                        <input
                          type="tel"
                          id="contact-telefono"
                          name="telefono"
                          required
                          className={inputBase}
                          placeholder={t("contact.phonePlaceholder")}
                        />
                      </div>
                      <div>
                        <label htmlFor="contact-ruc" className={labelBase}>
                          {t("contact.ruc")}
                        </label>
                        <input
                          type="text"
                          id="contact-ruc"
                          name="ruc"
                          maxLength={20}
                          className={inputBase}
                          placeholder={t("contact.rucPlaceholder")}
                        />
                      </div>
                    </div>

                    {/* Fila 3: Empresa + Servicio */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="contact-empresa" className={labelBase}>
                          {t("contact.company")}
                        </label>
                        <input
                          type="text"
                          id="contact-empresa"
                          name="empresa"
                          className={inputBase}
                          placeholder={t("contact.companyPlaceholder")}
                        />
                      </div>
                      <div>
                        <label htmlFor="contact-servicio" className={labelBase}>
                          {t("contact.service")}
                        </label>
                        <select
                          id="contact-servicio"
                          name="servicio"
                          className={`${inputBase} appearance-none cursor-pointer`}
                          defaultValue=""
                        >
                          <option value="" disabled className="bg-[#0B1628]">{t("contact.serviceDefault")}</option>
                          {serviceOptions.map((svc) => (
                            <option key={svc} value={svc} className="bg-[#0B1628]">{svc}</option>
                          ))}
                        </select>
                      </div>
                    </div>

                    {/* Mensaje */}
                    <div>
                      <label htmlFor="contact-message" className={labelBase}>
                        {t("contact.message")}
                      </label>
                      <textarea
                        id="contact-message"
                        name="message"
                        rows={3}
                        maxLength={500}
                        className={`${inputBase} resize-none`}
                        placeholder={t("contact.messagePlaceholder")}
                      />
                      <p className="text-xs text-foreground-500/40 mt-1 text-right">{t("contact.maxChars")}</p>
                    </div>
                  </div>

                  {/* Columna derecha: info de contacto */}
                  <div className="space-y-4">
                    {/* Contact info cards */}
                    <div className="space-y-2.5">
                      <a
                        href={contactInfo.whatsappHref}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-start gap-3 p-3 rounded-xl bg-[#25D366]/8 border border-[#25D366]/20 hover:border-[#25D366]/40 transition-all duration-300 cursor-pointer"
                      >
                        <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-[#25D366]/12 border border-[#25D366]/25 flex-shrink-0">
                          <i className="ri-whatsapp-line text-[#25D366]" />
                        </div>
                        <div>
                          <p className="text-[10px] font-label font-semibold text-foreground-500/60 uppercase tracking-wider mb-0.5">{t("contact.whatsapp")}</p>
                          <p className="text-sm text-foreground-100">{contactInfo.phone}</p>
                        </div>
                      </a>

                      <div className="flex items-start gap-3 p-3 rounded-xl bg-white/3 border border-white/6">
                        <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-primary-500/10 border border-primary-400/15 flex-shrink-0">
                          <i className="ri-mail-line text-primary-300" />
                        </div>
                        <div>
                          <p className="text-[10px] font-label font-semibold text-foreground-500/60 uppercase tracking-wider mb-0.5">{t("contact.email")}</p>
                          <a href={`mailto:${contactInfo.email}`} className="text-sm text-foreground-200 hover:text-primary-300 transition-colors">
                            {contactInfo.email}
                          </a>
                        </div>
                      </div>

                      <div className="flex items-start gap-3 p-3 rounded-xl bg-white/3 border border-white/6">
                        <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-accent-500/10 border border-accent-400/15 flex-shrink-0">
                          <i className="ri-time-line text-accent-300" />
                        </div>
                        <div>
                          <p className="text-[10px] font-label font-semibold text-foreground-500/60 uppercase tracking-wider mb-0.5">{t("contact.availability")}</p>
                          <p className="text-sm text-foreground-200">{t("contact.availabilityValue")}</p>
                        </div>
                      </div>

                      <div className="flex items-start gap-3 p-3 rounded-xl bg-white/3 border border-white/6">
                        <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-rose-500/10 border border-rose-400/15 flex-shrink-0">
                          <i className="ri-global-line text-rose-300" />
                        </div>
                        <div>
                          <p className="text-[10px] font-label font-semibold text-foreground-500/60 uppercase tracking-wider mb-0.5">{t("contact.coverage")}</p>
                          <p className="text-sm text-foreground-200">{t("contact.coverageValue")}</p>
                        </div>
                      </div>
                    </div>

                    {/* Social */}
                    <div>
                      <p className="text-[10px] font-label font-semibold text-foreground-500/60 uppercase tracking-wider mb-2.5">
                        {t("contact.followUs")}
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {socials.map((social) => (
                          <a
                            key={social.label}
                            href={social.href}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="w-9 h-9 flex items-center justify-center rounded-lg bg-white/5 border border-white/8 text-foreground-400/50 hover:text-white hover:border-primary-400/40 hover:bg-primary-500/15 transition-all duration-300 cursor-pointer"
                            aria-label={social.label}
                          >
                            <i className={`${social.icon} text-sm`} />
                          </a>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Honeypot */}
                <input
                  type="text"
                  name="company_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                  className="hp-field"
                />

                {formError && (
                  <div className="flex items-center gap-2 p-3 rounded-lg bg-rose-500/10 border border-rose-400/20 text-rose-300 text-sm mt-4">
                    <i className="ri-error-warning-line" />
                    <span>{formError}</span>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={formStatus === "submitting"}
                  className="w-full mt-6 inline-flex items-center justify-center gap-2 px-6 py-4 rounded-xl bg-gradient-to-r from-primary-500 to-accent-500 text-white font-label font-semibold text-sm hover:from-primary-600 hover:to-accent-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 cursor-pointer shadow-lg shadow-primary-500/20 hover:shadow-primary-500/30"
                >
                  {formStatus === "submitting" ? (
                    <>
                      <i className="ri-loader-4-line animate-spin" />
                      {t("contact.sending")}
                    </>
                  ) : (
                    <>
                      {t("contact.send")}
                      <i className="ri-send-plane-line" />
                    </>
                  )}
                </button>

                <p className="text-[11px] text-foreground-500/50 leading-relaxed text-center mt-3">
                  {t("contact.consentPre")}{" "}
                  <Link to="/politica-privacidad" className="text-primary-300 hover:text-primary-200 underline underline-offset-2 transition-colors cursor-pointer">
                    {t("legal.link.privacy")}
                  </Link>{" "}
                  {t("contact.consentAnd")}{" "}
                  <Link to="/terminos" className="text-primary-300 hover:text-primary-200 underline underline-offset-2 transition-colors cursor-pointer">
                    {t("legal.link.terms")}
                  </Link>
                  .
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}